/**
 * Chaves de texto que ligam a planilha ao estado do app (ADR-0075: ligação
 * só por chave em texto, nunca por FK).
 *
 * A chave do ambiente é opaca para o backend; tem de ser estável entre
 * cargas da planilha e tolerante a acento, caixa e espaço — é o PPCP quem
 * digita "Cozinha " numa semana e "COZINHA" na outra.
 */
import { hasValue, normalizeText } from "../../../utils/data";
import { parseMarceneiro } from "../../../utils/entregas";
import { F } from "../../../utils/gerencialFields";

// O backend guarda a chave em até 300 caracteres (contrato v2).
export const TAMANHO_MAXIMO_CHAVE = 300;
export const SEPARADOR_CHAVE = ":";

const SEM_MARCENEIRO = "SEM MARCENEIRO";

function textoLimpo(valor) {
  return hasValue(valor) ? String(valor).replace(/\s+/g, " ").trim() : "";
}

/**
 * "529:COZINHA" a partir da linha da planilha. `null` quando falta o pedido
 * ou o ambiente: sem os dois a chave colidiria entre linhas diferentes, e é
 * melhor não deixar apontar do que apontar no ambiente errado.
 */
export function chaveAmbiente(row) {
  const pedido = normalizeText(textoLimpo(row?.[F.NUMERO_DO_PEDIDO]));
  const ambiente = normalizeText(textoLimpo(row?.[F.AMBIENTE]));
  if (!pedido || !ambiente) return null;
  return `${pedido}${SEPARADOR_CHAVE}${ambiente}`.slice(0, TAMANHO_MAXIMO_CHAVE);
}

/** "529 · COZINHA" — o que o backend grava como rótulo e a tela mostra. */
export function rotuloAmbiente(row) {
  const pedido = textoLimpo(row?.[F.NUMERO_DO_PEDIDO]);
  const ambiente = textoLimpo(row?.[F.AMBIENTE]).toLocaleUpperCase("pt-BR");
  return [pedido, ambiente].filter(Boolean).join(" · ");
}

/**
 * Forma de comparar o apelido do colaborador com o nome que a planilha traz
 * em MARCENEIRO RESPONSÁVEL — a mesma normalização do backend (maiúsculas,
 * sem acento, espaços colapsados). Vazio → "".
 */
export function chaveApelido(texto) {
  return normalizeText(texto);
}

/**
 * Nomes de marceneiro que aparecem na planilha, para o cadastro de
 * colaborador sugerir o vínculo (opcional). Um item por grafia normalizada,
 * guardando a primeira grafia vista:
 * [{apelido: "NALDO (EMPREITA)", nome: "Naldo", tipo: "Empreita", total: 3}]
 */
export function apelidosDaPlanilha(linhas) {
  if (!Array.isArray(linhas)) return [];
  const porChave = new Map();
  linhas.forEach((row) => {
    const bruto = textoLimpo(row?.[F.MARCENEIRO_RESPONSAVEL]);
    const chave = chaveApelido(bruto);
    if (!chave || chave === SEM_MARCENEIRO) return;
    const atual = porChave.get(chave);
    if (atual) {
      atual.total += 1;
      return;
    }
    const { nome, tipo } = parseMarceneiro(bruto);
    porChave.set(chave, { apelido: bruto, nome, tipo, total: 1 });
  });
  return [...porChave.values()].sort((a, b) => a.apelido.localeCompare(b.apelido, "pt-BR"));
}
